import { useState, useMemo } from 'react'
import { YieldControlBar } from './components/YieldControlBar'
import { YieldKpiSection } from './components/YieldKpiSection'
import { BuildingYieldBars } from './components/BuildingYieldBars'
import { YieldDetailTable } from './components/YieldDetailTable'
import { mockBuildings, mockKpi } from './data/mockSpaceYieldData'
import type { Building } from './data/mockSpaceYieldData'

export const SpaceYieldReportPage = () => {
  // --- State ---
  const [location, setLocation] = useState('phx')
  const [period, setPeriod] = useState('2026-01')
  const [spaceType, setSpaceType] = useState<string[]>(['Hangar', 'Community', 'Tiedown']) // Default: no Ramp
  const [segment, setSegment] = useState('all')
  const [groupBy, setGroupBy] = useState('building')
  const [includeFuel, setIncludeFuel] = useState(false)

  // --- Filtering Logic (Client-side Mock) ---
  const filteredBuildings = useMemo(() => {
    // Deep clone to avoid mutating mock data
    const buildings: Building[] = JSON.parse(JSON.stringify(mockBuildings))
    
    // 1. Filter Units by Space Type
    buildings.forEach(b => {
      b.units = b.units.filter(u => spaceType.includes(u.type))
    })

    // 2. Filter Units by Revenue Segment
    if (segment !== 'all') {
      buildings.forEach(b => {
        b.units = b.units.filter(u => {
          if (segment === 'lease') return u.revenue_segment === 'lease' || u.revenue_segment === 'hybrid'
          if (segment === 'transient') return u.revenue_segment === 'transient' || u.revenue_segment === 'hybrid'
          if (segment === 'idle') return u.revenue_segment === 'idle'
          return true
        })
      })
    }

    // Recalculate Building Stats after filtering units
    // (This is a simplified recalculation for the demo)
    buildings.forEach(b => {
        const totalSqft = b.units.reduce((sum, u) => sum + u.sqft, 0)
        const revenue = b.units.reduce((sum, u) => sum + (u.lease_rate || 0) + (u.transient_revenue || 0), 0)
        const producingUnits = b.units.filter(u => u.status !== 'idle' && u.status !== 'maintenance' && u.status !== 'occupied_no_contract')
        const occupiedSqft = producingUnits.reduce((sum, u) => sum + u.sqft, 0)

        b.total_units = b.units.length
        b.total_sqft = totalSqft
        b.revenue = revenue
        b.occupancy_pct = totalSqft > 0 ? Math.round((occupiedSqft / totalSqft) * 100) : 0
        b.avg_yield = totalSqft > 0 ? revenue / totalSqft : 0
    })

    // Filter out buildings with no units left matching criteria
    return buildings.filter(b => b.total_units > 0)

  }, [spaceType, segment])


  return (
    <div className="flex flex-col h-full bg-slate-50/30">
        
      {/* 1. Control Bar (Sticky) */}
      <YieldControlBar 
        location={location} setLocation={setLocation}
        period={period} setPeriod={setPeriod}
        spaceType={spaceType} setSpaceType={setSpaceType}
        segment={segment} setSegment={setSegment}
        groupBy={groupBy} setGroupBy={setGroupBy}
        includeFuel={includeFuel} setIncludeFuel={setIncludeFuel}
      />

      <div className="flex-1 overflow-auto p-6 md:p-8 pt-6 space-y-8 max-w-[1600px] mx-auto w-full">
        
        {/* Header Content */}
        <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Space Yield</h1>
            <p className="text-slate-500">
                Phoenix Sky Harbor FBO · {period === '2026-01' ? 'January 2026' : 'Current Period'}
            </p>
        </div>

        {/* 2. KPI Section */}
        {/* Note: In a real app, KPIs would also re-aggregate based on filteredBuildings. 
            For this demo, we'll use static mockKpi but ideally we'd recalculate it. 
            Let's keep it static for simplicity as per plan, or maybe do a quick sum?
            Let's stick to mockKpi for MVP speed, as re-aggregating everything is complex.
        */}
        <YieldKpiSection data={mockKpi} includeFuel={includeFuel} />

        {/* 3. Building Visuals */}
        <BuildingYieldBars buildings={filteredBuildings} includeFuel={includeFuel} />

        {/* 4. Unit Detail Table */}
        <YieldDetailTable buildings={filteredBuildings} includeFuel={includeFuel} />
        
      </div>
    </div>
  )
}
